import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import * as vega from 'vega';
import * as vegaLite from 'vega-lite';
import * as vl from 'vega-lite-api';
import { Handler } from 'vega-tooltip';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')

const width = 700;
const height = 400;

const config = {
    axis: {
        domain: false,
        tickColor: 'lightGray',
    },
    style: {
        'guide-label': {
            fontSize: 12,
            fill: '#000'
        },
        'guide-title': {
            fontSize: 18,
            fill: '#000'
        }
    },
    legend: {
        disable: true
    }
};

// registration
vl.register(vega, vegaLite, {
    view: { renderer: 'svg' },
    init: view => { view.tooltip(new Handler().call); }
});

const GraphApiFatigue = ({ data, graphParams }) => {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const linePlotContainerRef = useRef(null);
    const [vegaSpec, setVegaSpec] = useState(null);
    const [linePlotVegaSpec, setLinePlotVegaSpec] = useState(null);
    const [selectedIndependentFactor, setSelectedIndependentFactor] = useState('author_total_shift_time');
    const [selectedDependentFactor, setSelectedDependentFactor] = useState('narrative_length');
    const [selectedLinePlotFactor, setSelectedLinePlotFactor] = useState('author_total_shift_time');
    const [viewType, setViewType] = useState('scatter'); // 'scatter' or 'heatmap'
    const [maxMedicsToShow, setMaxMedicsToShow] = useState(10); // Number of medics to show in line plot
    const [polynomialDegree, setPolynomialDegree] = useState(1); // 1, 2, or 3
    
    // Filter bounds for independent variables
    const [minShiftTime, setMinShiftTime] = useState(0);
    const [maxShiftTime, setMaxShiftTime] = useState(1440); // 24 hours in minutes
    const [minDrivingTime, setMinDrivingTime] = useState(0);
    const [maxDrivingTime, setMaxDrivingTime] = useState(1440);
    
    // Filter bounds for dependent variables
    const [minNarrativeLengthFilter, setMinNarrativeLengthFilter] = useState(0);
    const [maxNarrativeLengthFilter, setMaxNarrativeLengthFilter] = useState(10000);
    const [minIssues, setMinIssues] = useState(0);
    const [maxIssues, setMaxIssues] = useState(50);
    const [minSuggestions, setMinSuggestions] = useState(0);
    const [maxSuggestions, setMaxSuggestions] = useState(50);
    const [minReactionTime, setMinReactionTime] = useState(0);
    const [maxReactionTime, setMaxReactionTime] = useState(600000); // 10 minutes in milliseconds
    const [minTotalOnSceneTime, setMinTotalOnSceneTime] = useState(0);
    const [maxTotalOnSceneTime, setMaxTotalOnSceneTime] = useState(3600000); // 60 minutes in milliseconds
    const [minRecoveryTime, setMinRecoveryTime] = useState(0);
    const [maxRecoveryTime, setMaxRecoveryTime] = useState(1800000); // 30 minutes in milliseconds
    const [minReportingTime, setMinReportingTime] = useState(0);
    const [maxReportingTime, setMaxReportingTime] = useState(3600000); // 60 minutes in milliseconds

    // Calculate data ranges for dynamic slider bounds
    const dataRanges = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return {
                shiftTime: { min: 0, max: 1440 },
                drivingTime: { min: 0, max: 1440 },
                narrativeLength: { min: 0, max: 10000 },
                issues: { min: 0, max: 50 },
                suggestions: { min: 0, max: 50 },
                reactionTime: { min: 0, max: 600000 },
                totalOnSceneTime: { min: 0, max: 3600000 },
                recoveryTime: { min: 0, max: 1800000 },
                reportingTime: { min: 0, max: 3600000 }
            };
        }
        
        const validData = data.filter(d => 
            d.author_total_shift_time_minutes !== null &&
            d.author_total_driving_time_minutes !== null
        );
        
        if (validData.length === 0) {
            return {
                shiftTime: { min: 0, max: 1440 },
                drivingTime: { min: 0, max: 1440 },
                narrativeLength: { min: 0, max: 10000 },
                issues: { min: 0, max: 50 },
                suggestions: { min: 0, max: 50 },
                reactionTime: { min: 0, max: 600000 },
                totalOnSceneTime: { min: 0, max: 3600000 },
                recoveryTime: { min: 0, max: 1800000 },
                reportingTime: { min: 0, max: 3600000 }
            };
        }
        
        const shiftTimes = validData.map(d => d.author_total_shift_time_minutes).filter(v => v !== null);
        const drivingTimes = validData.map(d => d.author_total_driving_time_minutes).filter(v => v !== null);
        const narrativeLengths = validData.map(d => d.narrative_length || 0);
        const issues = validData.map(d => d.number_issues || 0);
        const suggestions = validData.map(d => d.number_suggestions || 0);
        const reactionTimes = validData.map(d => d.reaction_time || 0).filter(v => v !== null);
        const totalOnSceneTimes = validData.map(d => d.total_on_scene_time || 0).filter(v => v !== null);
        const recoveryTimes = validData.map(d => d.recovery_time || 0).filter(v => v !== null);
        const reportingTimes = validData.map(d => d.reporting_time || 0).filter(v => v !== null);
        
        return {
            shiftTime: {
                min: Math.min(...shiftTimes),
                max: Math.max(...shiftTimes)
            },
            drivingTime: {
                min: Math.min(...drivingTimes),
                max: Math.max(...drivingTimes)
            },
            narrativeLength: {
                min: Math.min(...narrativeLengths),
                max: Math.max(...narrativeLengths)
            },
            issues: {
                min: Math.min(...issues),
                max: Math.max(...issues)
            },
            suggestions: {
                min: Math.min(...suggestions),
                max: Math.max(...suggestions)
            },
            reactionTime: {
                min: Math.min(...reactionTimes),
                max: Math.max(...reactionTimes)
            },
            totalOnSceneTime: {
                min: Math.min(...totalOnSceneTimes),
                max: Math.max(...totalOnSceneTimes)
            },
            recoveryTime: {
                min: Math.min(...recoveryTimes),
                max: Math.max(...recoveryTimes)
            },
            reportingTime: {
                min: Math.min(...reportingTimes),
                max: Math.max(...reportingTimes)
            }
        };
    }, [data]);

    // Process data - data is already processed with fatigue indicators from the module
    const processedData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        
        const mapped = data.map(d => {
            // Ensure timestamp is available for line plot
            const timestamp = d.creation_timestamp ? new Date(d.creation_timestamp).getTime() : null;
            
            return {
                ...d,
                // narrative_length is calculated on the server side
                narrative_length: d.narrative_length || 0,
                number_issues: d.number_issues || 0,
                number_suggestions: d.number_suggestions || 0,
                timestamp: timestamp, // Add timestamp in milliseconds for plotting
                timestamp_date: d.creation_timestamp // Keep original date string
            };
        });
        
        return mapped.filter(d => {
            // Filter out invalid data points using user-adjustable filters
            // Also filter out reports without fatigue indicators (no author or missing shift data)
            if (d.author_total_shift_time_minutes === null) return false;
            if (d.author_total_driving_time_minutes === null) return false;
            if (d.timestamp === null) return false;
            
            // Apply filters based on selected independent variable
            if (selectedIndependentFactor === 'author_total_shift_time') {
                if (d.author_total_shift_time_minutes < minShiftTime || d.author_total_shift_time_minutes > maxShiftTime) {
                    return false;
                }
            } else if (selectedIndependentFactor === 'author_total_driving_time') {
                if (d.author_total_driving_time_minutes < minDrivingTime || d.author_total_driving_time_minutes > maxDrivingTime) {
                    return false;
                }
            }
            
            // Apply filters based on selected dependent variable
            if (selectedDependentFactor === 'narrative_length') {
                if (d.narrative_length < minNarrativeLengthFilter || d.narrative_length > maxNarrativeLengthFilter) {
                    return false;
                }
            } else if (selectedDependentFactor === 'number_issues') {
                if (d.number_issues < minIssues || d.number_issues > maxIssues) {
                    return false;
                }
            } else if (selectedDependentFactor === 'number_suggestions') {
                if (d.number_suggestions < minSuggestions || d.number_suggestions > maxSuggestions) {
                    return false;
                }
            } else if (selectedDependentFactor === 'reaction_time') {
                const reactionTime = d.reaction_time || 0;
                if (reactionTime < minReactionTime || reactionTime > maxReactionTime) {
                    return false;
                }
            } else if (selectedDependentFactor === 'total_on_scene_time') {
                const totalOnSceneTime = d.total_on_scene_time || 0;
                if (totalOnSceneTime < minTotalOnSceneTime || totalOnSceneTime > maxTotalOnSceneTime) {
                    return false;
                }
            } else if (selectedDependentFactor === 'recovery_time') {
                const recoveryTime = d.recovery_time || 0;
                if (recoveryTime < minRecoveryTime || recoveryTime > maxRecoveryTime) {
                    return false;
                }
            } else if (selectedDependentFactor === 'reporting_time') {
                const reportingTime = d.reporting_time || 0;
                if (reportingTime < minReportingTime || reportingTime > maxReportingTime) {
                    return false;
                }
            }
            
            return true;
        });
    }, [data, selectedIndependentFactor, selectedDependentFactor, 
        minShiftTime, maxShiftTime, minDrivingTime, maxDrivingTime,
        minNarrativeLengthFilter, maxNarrativeLengthFilter, minIssues, maxIssues, minSuggestions, maxSuggestions,
        minReactionTime, maxReactionTime, minTotalOnSceneTime, maxTotalOnSceneTime,
        minRecoveryTime, maxRecoveryTime, minReportingTime, maxReportingTime]);

    // Independent factor options (fatigue indicators)
    const independentFactors = [
        { 
            value: 'author_total_shift_time', 
            label: 'Total Time on Shift', 
            field: 'author_total_shift_time_minutes',
            title: 'Total Time on Current Shift (minutes)'
        },
        { 
            value: 'author_total_driving_time', 
            label: 'Total Driving Time', 
            field: 'author_total_driving_time_minutes',
            title: 'Total Driving Time on Current Shift (minutes)'
        }
    ];

    // Dependent factor options
    const dependentFactors = [
        { 
            value: 'narrative_length', 
            label: 'Narrative Length', 
            field: 'narrative_length',
            title: 'Narrative Length (characters)'
        },
        { 
            value: 'number_issues', 
            label: 'Number of Issues', 
            field: 'number_issues',
            title: 'Number of Issues'
        },
        { 
            value: 'number_suggestions', 
            label: 'Number of Suggestions', 
            field: 'number_suggestions',
            title: 'Number of Suggestions'
        },
        { 
            value: 'reaction_time', 
            label: 'Reaction Time', 
            field: 'reaction_time',
            title: 'Reaction Time (milliseconds)'
        },
        { 
            value: 'total_on_scene_time', 
            label: 'Total On Scene Time', 
            field: 'total_on_scene_time',
            title: 'Total On Scene Time (milliseconds)'
        },
        { 
            value: 'recovery_time', 
            label: 'Recovery Time', 
            field: 'recovery_time',
            title: 'Recovery Time (milliseconds)'
        },
        { 
            value: 'reporting_time', 
            label: 'Reporting Time', 
            field: 'reporting_time',
            title: 'Reporting Time (milliseconds)'
        }
    ];

    // Get the selected independent and dependent factor configurations
    const selectedIndependent = useMemo(() => {
        return independentFactors.find(f => f.value === selectedIndependentFactor) || independentFactors[0];
    }, [selectedIndependentFactor]);

    const selectedDependent = useMemo(() => {
        return dependentFactors.find(f => f.value === selectedDependentFactor) || dependentFactors[0];
    }, [selectedDependentFactor]);

    // Update slider bounds when data ranges change
    useEffect(() => {
        if (dataRanges.shiftTime.max > 0) {
            setMinShiftTime(Math.floor(dataRanges.shiftTime.min));
            setMaxShiftTime(Math.ceil(dataRanges.shiftTime.max));
        }
        if (dataRanges.drivingTime.max > 0) {
            setMinDrivingTime(Math.floor(dataRanges.drivingTime.min));
            setMaxDrivingTime(Math.ceil(dataRanges.drivingTime.max));
        }
        if (dataRanges.narrativeLength.max > 0) {
            setMinNarrativeLengthFilter(Math.floor(dataRanges.narrativeLength.min));
            setMaxNarrativeLengthFilter(Math.ceil(dataRanges.narrativeLength.max));
        }
        if (dataRanges.issues.max > 0) {
            setMinIssues(Math.floor(dataRanges.issues.min));
            setMaxIssues(Math.ceil(dataRanges.issues.max));
        }
        if (dataRanges.suggestions.max > 0) {
            setMinSuggestions(Math.floor(dataRanges.suggestions.min));
            setMaxSuggestions(Math.ceil(dataRanges.suggestions.max));
        }
        if (dataRanges.reactionTime.max > 0) {
            setMinReactionTime(Math.floor(dataRanges.reactionTime.min));
            setMaxReactionTime(Math.ceil(dataRanges.reactionTime.max));
        }
        if (dataRanges.totalOnSceneTime.max > 0) {
            setMinTotalOnSceneTime(Math.floor(dataRanges.totalOnSceneTime.min));
            setMaxTotalOnSceneTime(Math.ceil(dataRanges.totalOnSceneTime.max));
        }
        if (dataRanges.recoveryTime.max > 0) {
            setMinRecoveryTime(Math.floor(dataRanges.recoveryTime.min));
            setMaxRecoveryTime(Math.ceil(dataRanges.recoveryTime.max));
        }
        if (dataRanges.reportingTime.max > 0) {
            setMinReportingTime(Math.floor(dataRanges.reportingTime.min));
            setMaxReportingTime(Math.ceil(dataRanges.reportingTime.max));
        }
    }, [dataRanges]);

    // Calculate covariance between selected independent and dependent variables
    const covariance = useMemo(() => {
        if (processedData.length === 0) return null;
        
        const xField = selectedIndependent.field;
        const yField = selectedDependent.field;
        
        // Extract valid data points (both x and y must be numbers)
        const validData = processedData.filter(d => 
            d[xField] !== null && 
            d[xField] !== undefined && 
            !isNaN(d[xField]) &&
            d[yField] !== null && 
            d[yField] !== undefined && 
            !isNaN(d[yField])
        );
        
        if (validData.length === 0) return null;
        
        // Calculate means
        const meanX = validData.reduce((sum, d) => sum + d[xField], 0) / validData.length;
        const meanY = validData.reduce((sum, d) => sum + d[yField], 0) / validData.length;
        
        // Calculate covariance: E[(X - E[X])(Y - E[Y])]
        const covariance = validData.reduce((sum, d) => {
            return sum + (d[xField] - meanX) * (d[yField] - meanY);
        }, 0) / validData.length;
        
        return {
            value: covariance,
            n: validData.length,
            meanX: meanX,
            meanY: meanY
        };
    }, [processedData, selectedIndependent, selectedDependent]);

    // Gaussian elimination to solve linear system Ax = b
    const solveLinearSystem = (A, b) => {
        const n = A.length;
        const augmented = A.map((row, i) => [...row, b[i]]);
        
        // Forward elimination
        for (let i = 0; i < n; i++) {
            // Find pivot
            let maxRow = i;
            for (let k = i + 1; k < n; k++) {
                if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
                    maxRow = k;
                }
            }
            
            // Swap rows
            [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];
            
            // Check for singular matrix
            if (Math.abs(augmented[i][i]) < 1e-10) {
                return null;
            }
            
            // Eliminate
            for (let k = i + 1; k < n; k++) {
                const factor = augmented[k][i] / augmented[i][i];
                for (let j = i; j <= n; j++) {
                    augmented[k][j] -= factor * augmented[i][j];
                }
            }
        }
        
        // Back substitution
        const x = new Array(n);
        for (let i = n - 1; i >= 0; i--) {
            x[i] = augmented[i][n];
            for (let j = i + 1; j < n; j++) {
                x[i] -= augmented[i][j] * x[j];
            }
            x[i] /= augmented[i][i];
        }
        
        return x;
    };

    // Polynomial fitting using least squares
    const polynomialFit = useMemo(() => {
        if (processedData.length === 0) return null;
        
        const xField = selectedIndependent.field;
        const yField = selectedDependent.field;
        
        // Extract valid data points
        const validData = processedData.filter(d => 
            d[xField] !== null && 
            d[xField] !== undefined && 
            !isNaN(d[xField]) &&
            d[yField] !== null && 
            d[yField] !== undefined && 
            !isNaN(d[yField])
        );
        
        if (validData.length < polynomialDegree + 1) return null; // Need at least degree+1 points
        
        const xValues = validData.map(d => d[xField]);
        const yValues = validData.map(d => d[yField]);
        
        // Build the Vandermonde matrix for polynomial fitting
        // For degree n, we need columns: [1, x, x^2, ..., x^n]
        const n = validData.length;
        const degree = polynomialDegree;
        const A = [];
        
        for (let i = 0; i < n; i++) {
            const row = [];
            for (let j = 0; j <= degree; j++) {
                row.push(Math.pow(xValues[i], j));
            }
            A.push(row);
        }
        
        // Solve using normal equations: (A^T * A) * coeffs = A^T * y
        // Compute A^T * A
        const AtA = [];
        for (let i = 0; i <= degree; i++) {
            const row = [];
            for (let j = 0; j <= degree; j++) {
                let sum = 0;
                for (let k = 0; k < n; k++) {
                    sum += A[k][i] * A[k][j];
                }
                row.push(sum);
            }
            AtA.push(row);
        }
        
        // Compute A^T * y
        const Aty = [];
        for (let i = 0; i <= degree; i++) {
            let sum = 0;
            for (let k = 0; k < n; k++) {
                sum += A[k][i] * yValues[k];
            }
            Aty.push(sum);
        }
        
        // Solve the system using Gaussian elimination
        const coeffs = solveLinearSystem(AtA, Aty);
        
        if (!coeffs) return null;
        
        // Calculate R and R²
        const meanY = yValues.reduce((sum, y) => sum + y, 0) / n;
        
        // Calculate predicted values and residuals
        let ssRes = 0; // Sum of squares of residuals
        let ssTot = 0; // Total sum of squares
        
        for (let i = 0; i < n; i++) {
            let predicted = 0;
            for (let j = 0; j <= degree; j++) {
                predicted += coeffs[j] * Math.pow(xValues[i], j);
            }
            const residual = yValues[i] - predicted;
            ssRes += residual * residual;
            ssTot += (yValues[i] - meanY) * (yValues[i] - meanY);
        }
        
        const rSquared = 1 - (ssRes / ssTot);
        // Calculate correlation coefficient R
        // R = sqrt(R²) with sign determined by the correlation direction
        // Use the covariance sign to determine R sign
        const meanX = xValues.reduce((sum, x) => sum + x, 0) / n;
        let sumXY = 0;
        for (let i = 0; i < n; i++) {
            sumXY += (xValues[i] - meanX) * (yValues[i] - meanY);
        }
        const rSign = sumXY >= 0 ? 1 : -1;
        const r = Math.sqrt(Math.max(0, rSquared)) * rSign;
        
        // Generate points for plotting the fitted polynomial
        const xMin = Math.min(...xValues);
        const xMax = Math.max(...xValues);
        const numPoints = 100;
        const fittedPoints = [];
        
        for (let i = 0; i <= numPoints; i++) {
            const x = xMin + (xMax - xMin) * (i / numPoints);
            let y = 0;
            for (let j = 0; j <= degree; j++) {
                y += coeffs[j] * Math.pow(x, j);
            }
            fittedPoints.push({ x: x, y: y });
        }
        
        return {
            coefficients: coeffs,
            r: r,
            rSquared: rSquared,
            fittedPoints: fittedPoints,
            xMin: xMin,
            xMax: xMax
        };
    }, [processedData, selectedIndependent, selectedDependent, polynomialDegree, viewType]);

    useEffect(() => {
        if (processedData.length === 0) {
            setVegaSpec(null);
            return;
        }

        let plot;

        if (viewType === 'heatmap') {
            // Define heatmap using rectangles
            const heatmapPlot = vl.markRect({
                tooltip: true
            })
                .data(processedData)
                .encode(
                    vl.x().fieldQ(selectedIndependent.field)
                        .title(selectedIndependent.title)
                        .bin({ maxbins: 30 })
                        .scale({ zero: false }),
                    vl.y().fieldQ(selectedDependent.field)
                        .title(selectedDependent.title)
                        .bin({ maxbins: 30 })
                        .scale({ zero: false }),
                    vl.color().aggregate('count')
                        .title('Number of Reports')
                        .scale({ scheme: 'blues' })
                )
                .width(width)
                .height(height);
            
            // If we have a polynomial fit, layer it with the heatmap
            if (polynomialFit && polynomialFit.fittedPoints.length > 0) {
                const fittedLine = vl.markLine({
                    color: 'red',
                    strokeWidth: 2,
                    tooltip: false
                })
                    .data(polynomialFit.fittedPoints)
                    .encode(
                        vl.x().fieldQ('x')
                            .title(selectedIndependent.title)
                            .scale({ zero: false }),
                        vl.y().fieldQ('y')
                            .title(selectedDependent.title)
                            .scale({ zero: false })
                    );
                
                plot = vl.layer(heatmapPlot, fittedLine)
                    .title(`${selectedIndependent.label} vs ${selectedDependent.label} (Heatmap)`);
            } else {
                plot = heatmapPlot
                    .title(`${selectedIndependent.label} vs ${selectedDependent.label} (Heatmap)`);
            }
        } else {
            // Define scatter plot
            const scatterPlot = vl.markPoint({
                filled: true,
                stroke: false,
                size: 100,
                opacity: 0.6,
                tooltip: true
            })
                .data(processedData)
                .encode(
                    vl.x().fieldQ(selectedIndependent.field)
                        .title(selectedIndependent.title)
                        .scale({ zero: false }),
                    vl.y().fieldQ(selectedDependent.field)
                        .title(selectedDependent.title)
                        .scale({ zero: false })
                )
                .width(width)
                .height(height);
            
            // If we have a polynomial fit, layer it with the scatter plot
            if (polynomialFit && polynomialFit.fittedPoints.length > 0) {
                const fittedLine = vl.markLine({
                    color: 'red',
                    strokeWidth: 2,
                    tooltip: false
                })
                    .data(polynomialFit.fittedPoints)
                    .encode(
                        vl.x().fieldQ('x')
                            .title(selectedIndependent.title)
                            .scale({ zero: false }),
                        vl.y().fieldQ('y')
                            .title(selectedDependent.title)
                            .scale({ zero: false })
                    );
                
                plot = vl.layer(scatterPlot, fittedLine)
                    .title(`${selectedIndependent.label} vs ${selectedDependent.label}`);
            } else {
                plot = scatterPlot
                    .title(`${selectedIndependent.label} vs ${selectedDependent.label}`);
            }
        }

        const vegaLiteSpec = plot
            .config(config)
            .toSpec();
        setVegaSpec(vegaLite.compile(vegaLiteSpec).spec);
    }, [processedData, selectedIndependent, selectedDependent, viewType, polynomialFit]);

    // Create line plot for independent variables over time
    useEffect(() => {
        if (processedData.length === 0) {
            setLinePlotVegaSpec(null);
            return;
        }

        // Get selected line plot factor
        const independentFactorsList = independentFactors;
        const selectedLineFactor = independentFactorsList.find(f => f.value === selectedLinePlotFactor) || independentFactorsList[0];

        // Prepare data for line plot - group by medic (author) and sort by timestamp
        const allLinePlotData = processedData
            .filter(d => d.author && d.timestamp !== null && d[selectedLineFactor.field] !== null)
            .map(d => {
                // Get first author ID
                const authorId = d.author ? d.author.split(',')[0].trim() : null;
                return {
                    ...d,
                    author_id: authorId,
                    timestamp_date: d.timestamp_date || d.creation_timestamp
                };
            })
            .sort((a, b) => a.timestamp - b.timestamp);

        if (allLinePlotData.length === 0) {
            setLinePlotVegaSpec(null);
            return;
        }

        // Count reports per medic to determine which medics to show
        const medicReportCounts = {};
        allLinePlotData.forEach(d => {
            if (d.author_id) {
                medicReportCounts[d.author_id] = (medicReportCounts[d.author_id] || 0) + 1;
            }
        });

        // Get top N medics by report count
        const topMedics = Object.entries(medicReportCounts)
            .sort((a, b) => b[1] - a[1]) // Sort by report count descending
            .slice(0, maxMedicsToShow)
            .map(([medicId]) => medicId);

        // Filter data to only include top N medics
        const linePlotData = allLinePlotData.filter(d => topMedics.includes(d.author_id));

        if (linePlotData.length === 0) {
            setLinePlotVegaSpec(null);
            return;
        }

        // Create line plot with one line per medic
        const linePlot = vl.markLine({
            point: true,
            tooltip: true
        })
            .data(linePlotData)
            .encode(
                vl.x().fieldT('timestamp_date')
                    .title('Time')
                    .timeUnit('yearmonthdatehoursminutes'),
                vl.y().fieldQ(selectedLineFactor.field)
                    .title(selectedLineFactor.title)
                    .scale({ zero: false }),
                vl.color().fieldN('author_id')
                    .title('Medic ID')
                    .legend({ disable: false }) // Enable legend for medic IDs
            )
            .width(width)
            .height(height)
            .title(`${selectedLineFactor.label} Over Time by Medic (Top ${maxMedicsToShow} Medics)`);

        const vegaLiteSpec = linePlot
            .config(config)
            .toSpec();
        setLinePlotVegaSpec(vegaLite.compile(vegaLiteSpec).spec);
    }, [processedData, selectedLinePlotFactor, maxMedicsToShow]);

    useEffect(() => {
        if (vegaSpec && containerRef.current) {
            const runtime = vega.parse(vegaSpec);
            const view = new vega.View(runtime)
                .renderer("svg")
                .initialize(containerRef.current)
                .hover()

            view.run();

            return () => {
                view.finalize();
            };
        }
    }, [vegaSpec]);

    useEffect(() => {
        if (linePlotVegaSpec && linePlotContainerRef.current) {
            const runtime = vega.parse(linePlotVegaSpec);
            const view = new vega.View(runtime)
                .renderer("svg")
                .initialize(linePlotContainerRef.current)
                .hover()

            view.run();

            return () => {
                view.finalize();
            };
        }
    }, [linePlotVegaSpec]);

    return (
        <div>
            {/* View Type and Factor Selection */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '15px' }}>
                    <h3 style={{ margin: 0, color: '#333' }}>Select Independent Factor (X-axis)</h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => setViewType('scatter')}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: viewType === 'scatter' ? '#007bff' : '#e9ecef',
                                color: viewType === 'scatter' ? 'white' : '#495057',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: viewType === 'scatter' ? 'bold' : 'normal'
                            }}
                        >
                            Scatter Plot
                        </button>
                        <button
                            onClick={() => setViewType('heatmap')}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: viewType === 'heatmap' ? '#007bff' : '#e9ecef',
                                color: viewType === 'heatmap' ? 'white' : '#495057',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: viewType === 'heatmap' ? 'bold' : 'normal'
                            }}
                        >
                            Heatmap
                        </button>
                    </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Independent Factor (X-axis)</h4>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {independentFactors.map((factor) => (
                            <button
                                key={factor.value}
                                onClick={() => setSelectedIndependentFactor(factor.value)}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: selectedIndependentFactor === factor.value ? '#007bff' : '#e9ecef',
                                    color: selectedIndependentFactor === factor.value ? 'white' : '#495057',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: selectedIndependentFactor === factor.value ? 'bold' : 'normal'
                                }}
                            >
                                {factor.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Dependent Factor (Y-axis)</h4>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {dependentFactors.map((factor) => (
                            <button
                                key={factor.value}
                                onClick={() => setSelectedDependentFactor(factor.value)}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: selectedDependentFactor === factor.value ? '#007bff' : '#e9ecef',
                                    color: selectedDependentFactor === factor.value ? 'white' : '#495057',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: selectedDependentFactor === factor.value ? 'bold' : 'normal'
                                }}
                            >
                                {factor.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Polynomial Fit Degree</h4>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => setPolynomialDegree(1)}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: polynomialDegree === 1 ? '#28a745' : '#e9ecef',
                                    color: polynomialDegree === 1 ? 'white' : '#495057',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: polynomialDegree === 1 ? 'bold' : 'normal'
                                }}
                            >
                                1st Degree
                            </button>
                            <button
                                onClick={() => setPolynomialDegree(2)}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: polynomialDegree === 2 ? '#28a745' : '#e9ecef',
                                    color: polynomialDegree === 2 ? 'white' : '#495057',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: polynomialDegree === 2 ? 'bold' : 'normal'
                                }}
                            >
                                2nd Degree
                            </button>
                            <button
                                onClick={() => setPolynomialDegree(3)}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: polynomialDegree === 3 ? '#28a745' : '#e9ecef',
                                    color: polynomialDegree === 3 ? 'white' : '#495057',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: polynomialDegree === 3 ? 'bold' : 'normal'
                                }}
                            >
                                3rd Degree
                            </button>
                        </div>
                    </div>
                {/* Filter Controls */}
                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                    {/* Independent Variable Filters - only show for selected variable */}
                    {selectedIndependentFactor === 'author_total_shift_time' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedIndependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {minShiftTime} min
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.shiftTime.min)}
                                        max={Math.ceil(dataRanges.shiftTime.max)}
                                        value={minShiftTime}
                                        onChange={(e) => setMinShiftTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {maxShiftTime} min
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.shiftTime.min)}
                                        max={Math.ceil(dataRanges.shiftTime.max)}
                                        value={maxShiftTime}
                                        onChange={(e) => setMaxShiftTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedIndependentFactor === 'author_total_driving_time' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedIndependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {minDrivingTime} min
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.drivingTime.min)}
                                        max={Math.ceil(dataRanges.drivingTime.max)}
                                        value={minDrivingTime}
                                        onChange={(e) => setMinDrivingTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {maxDrivingTime} min
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.drivingTime.min)}
                                        max={Math.ceil(dataRanges.drivingTime.max)}
                                        value={maxDrivingTime}
                                        onChange={(e) => setMaxDrivingTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Dependent Variable Filters - only show for selected variable */}
                    {selectedDependentFactor === 'narrative_length' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {minNarrativeLengthFilter} chars
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.narrativeLength.min)}
                                        max={Math.ceil(dataRanges.narrativeLength.max)}
                                        value={minNarrativeLengthFilter}
                                        onChange={(e) => setMinNarrativeLengthFilter(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {maxNarrativeLengthFilter} chars
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.narrativeLength.min)}
                                        max={Math.ceil(dataRanges.narrativeLength.max)}
                                        value={maxNarrativeLengthFilter}
                                        onChange={(e) => setMaxNarrativeLengthFilter(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedDependentFactor === 'number_issues' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {minIssues}
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.issues.min)}
                                        max={Math.ceil(dataRanges.issues.max)}
                                        value={minIssues}
                                        onChange={(e) => setMinIssues(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {maxIssues}
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.issues.min)}
                                        max={Math.ceil(dataRanges.issues.max)}
                                        value={maxIssues}
                                        onChange={(e) => setMaxIssues(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedDependentFactor === 'number_suggestions' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {minSuggestions}
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.suggestions.min)}
                                        max={Math.ceil(dataRanges.suggestions.max)}
                                        value={minSuggestions}
                                        onChange={(e) => setMinSuggestions(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {maxSuggestions}
                                    </label>
                                    <input
                                        type="range"
                                        min={Math.floor(dataRanges.suggestions.min)}
                                        max={Math.ceil(dataRanges.suggestions.max)}
                                        value={maxSuggestions}
                                        onChange={(e) => setMaxSuggestions(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedDependentFactor === 'reaction_time' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {(minReactionTime / 1000).toFixed(1)} sec
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.reactionTime.max)}
                                        value={minReactionTime}
                                        onChange={(e) => setMinReactionTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {(maxReactionTime / 1000).toFixed(1)} sec
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.reactionTime.max)}
                                        value={maxReactionTime}
                                        onChange={(e) => setMaxReactionTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedDependentFactor === 'total_on_scene_time' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {(minTotalOnSceneTime / 60000).toFixed(1)} min
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.totalOnSceneTime.max)}
                                        value={minTotalOnSceneTime}
                                        onChange={(e) => setMinTotalOnSceneTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {(maxTotalOnSceneTime / 60000).toFixed(1)} min
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.totalOnSceneTime.max)}
                                        value={maxTotalOnSceneTime}
                                        onChange={(e) => setMaxTotalOnSceneTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedDependentFactor === 'recovery_time' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {(minRecoveryTime / 60000).toFixed(1)} min
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.recoveryTime.max)}
                                        value={minRecoveryTime}
                                        onChange={(e) => setMinRecoveryTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {(maxRecoveryTime / 60000).toFixed(1)} min
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.recoveryTime.max)}
                                        value={maxRecoveryTime}
                                        onChange={(e) => setMaxRecoveryTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {selectedDependentFactor === 'reporting_time' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
                                Filter {selectedDependent.label}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Min: {(minReportingTime / 60000).toFixed(1)} min
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.reportingTime.max)}
                                        value={minReportingTime}
                                        onChange={(e) => setMinReportingTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                                        Max: {(maxReportingTime / 60000).toFixed(1)} min
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={Math.ceil(dataRanges.reportingTime.max)}
                                        value={maxReportingTime}
                                        onChange={(e) => setMaxReportingTime(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div ref={containerRef} />
            
            {/* Covariance and Fit Statistics Display */}
            {covariance !== null && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', textAlign: 'center' }}>
                        <div>
                            <div style={{ fontSize: '12px', color: '#495057', marginBottom: '5px' }}>
                                <strong>Covariance</strong>
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                                {covariance.value.toFixed(2)}
                            </div>
                        </div>
                        {polynomialFit !== null && (
                            <>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#495057', marginBottom: '5px' }}>
                                        <strong>R</strong>
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                                        {polynomialFit.r.toFixed(4)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#495057', marginBottom: '5px' }}>
                                        <strong>R²</strong>
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                                        {polynomialFit.rSquared.toFixed(4)}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                        (n = {covariance.n} data points)
                    </div>
                </div>
            )}
            
            {/* Line Plot Section */}
            <div style={{ marginTop: '40px' }}>
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '15px' }}>
                        <h3 style={{ margin: 0, color: '#333' }}>Fatigue Over Time by Medic</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {independentFactors.map((factor) => (
                                <button
                                    key={factor.value}
                                    onClick={() => setSelectedLinePlotFactor(factor.value)}
                                    style={{
                                        padding: '8px 16px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        backgroundColor: selectedLinePlotFactor === factor.value ? '#007bff' : '#e9ecef',
                                        color: selectedLinePlotFactor === factor.value ? 'white' : '#495057',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: selectedLinePlotFactor === factor.value ? 'bold' : 'normal'
                                    }}
                                >
                                    {factor.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666', marginBottom: '15px' }}>
                        Each line represents a different medic. Points show reports where that medic was the author.
                    </p>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#495057' }}>
                            Max Medics to Show: {maxMedicsToShow} (showing top medics by report count)
                        </label>
                        <input
                            type="range"
                            min="1"
                            max="20"
                            value={maxMedicsToShow}
                            onChange={(e) => setMaxMedicsToShow(Number(e.target.value))}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
                <div ref={linePlotContainerRef} />
            </div>
            
            {/* Summary Statistics */}
            {processedData.length > 0 && (
                <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: '15px', color: '#333' }}>Fatigue Analysis Summary</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                                {processedData.length}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Total Data Points</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                                {Math.round(processedData.reduce((sum, d) => sum + d[selectedIndependent.field], 0) / processedData.length * 10) / 10}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Avg {selectedIndependent.label}</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                                {Math.round(processedData.reduce((sum, d) => sum + d[selectedDependent.field], 0) / processedData.length * 10) / 10}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Avg {selectedDependent.label}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GraphApiFatigue;

